import React from 'react';
import PropTypes from 'prop-types';
import WizardData from '../../data/WizardData';
import UIHelper from '../../utils/UIHelper';
import ASSET_TYPES from '../../types/AssetTypes';
import mui from '../../utils/MuiIcons';
import Thumbnail from '../../components/Thumbnail';
import AssetValidityFormComponent from '../../../../../components/AssetValidityFormComponent/AssetValidityFormComponent';

/**
 * @typedef {import('../../types/ContentItems').ContentItem} ContentItem
 */

/**
 * Functional component for displaying selected assets in a carousel.
 * Shows a horizontal list of selected assets with thumbnails, names, types, and remove buttons.
 * Displays an empty state message if no assets are selected.
 * Supports drag and drop reordering.
 * @param {Object} props - Component props.
 * @param {WizardData} props.wizardData - Instance of WizardData containing selected asset objects.
 * @param {(index: number) => void} props.onRemove - Callback function to remove an asset by index.
 * @param {(fromIndex: number, toIndex: number) => void} props.onMove - Callback function to move an asset.
 * @param {(index: number, newDuration: number) => void} props.onDurationUpdate - Callback function to update asset duration in seconds.
 * @param {(index: number, updatedAsset: ContentItem) => void} props.onValidityUpdate - Callback function to update asset validity.
 * @returns {JSX.Element} The rendered carousel UI.
 */
function AssetCarousel({ wizardData, onRemove, onMove, onDurationUpdate, onValidityUpdate }) {
  const [draggedIndex, setDraggedIndex] = React.useState(null);
  const draggedElementRef = React.useRef(null);
  const cleanDragOverTimeout = React.useRef([]);

  /**
   * Clears any active timeouts for removing drag over indicators to prevent stale indicators from remaining after drag operations.
   */
  const clearDragOverTimeouts = () => {
    cleanDragOverTimeout.current.forEach(timeoutId => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    cleanDragOverTimeout.current = [];
  };

  React.useEffect(() => {
    return () => {
      clearDragOverTimeouts();

      if (draggedElementRef.current) {
        draggedElementRef.current.classList.remove('dragging');
        draggedElementRef.current = null;
      }
    };
  }, []);

  /**
   * Renders the duration input for a given asset.
   * @param {ContentItem} asset - The asset object.
   * @param {number} index - The index of the asset.
   * @returns {JSX.Element} The duration input UI.
   */
  const renderDurationInput = (asset, index) => {
    const editable = asset.type === ASSET_TYPES.image;
    const durationValue = UIHelper.time.inSeconds(asset.duration, {
      withUnits: false,
      decimalPlaces: 0,
    });
    const onChangeHandler = editable
      ? e => onDurationUpdate?.(index, Number(e.target.value))
      : undefined;

    return (
      <input
        type="number"
        className={`form-control input-sm ${
          !asset.duration || asset.duration <= 0 ? 'has-error' : ''
        }`}
        value={durationValue}
        onChange={onChangeHandler}
        disabled={!editable}
        min="1"
        data-test="asset-carousel-duration-input"
      />
    );
  };

  /**
   * Clears the drag state by removing any visual indicators and resetting the dragged index.
   * This is called after a drag operation ends or is cancelled to ensure the UI is reset properly.
   */
  const clearDragState = () => {
    if (draggedElementRef.current) {
      draggedElementRef.current.classList.remove('dragging');
      draggedElementRef.current = null;
    }

    setDraggedIndex(null);
  };

  /**
   * Handles the start of a drag operation for reordering assets.
   * @param {DragEvent} e - The drag event.
   * @param {number} index - The index of the asset being dragged.
   */
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    draggedElementRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    draggedElementRef.current.classList.add('dragging');
  };

  /**
   * Handles the drop event to reorder assets.
   * @param {DragEvent} e - The drag event.
   * @param {number} index - The index where the asset is dropped.
   */
  const handleDrop = (e, index) => {
    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    if (onMove) {
      onMove(draggedIndex, index);
    }

    clearDragState();
  };

  /**
   * Handles the end of a drag operation, clearing any drag state and visual indicators.
   */
  const handleDragEnd = () => {
    clearDragState();
  };

  /**
   * Recursively finds the closest carousel item element from the event target.
   * @param {HTMLElement} element - The starting element from the event target.
   * @returns {HTMLElement|null} The closest carousel item element or null if not found.
   */
  const findClosestCarouselItem = element => {
    if (!element) {
      return null;
    }

    if (element.classList.contains('carousel-item')) {
      return element;
    }

    return findClosestCarouselItem(element.parentElement);
  };

  /**
   * Handles the drag over event to allow dropping.
   * @param {DragEvent} e - The drag event.
   */
  const handleDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dragOverItem = findClosestCarouselItem(e.target);

    if (dragOverItem && draggedIndex !== null) {
      const dragOverparent = dragOverItem.parentElement;
      const index = Array.from(dragOverparent.children).indexOf(dragOverItem);
      const indicatorClass = index < draggedIndex ? 'drag-over-left' : 'drag-over-right';

      dragOverItem.classList.add(indicatorClass);

      if (cleanDragOverTimeout.current[index]) {
        clearTimeout(cleanDragOverTimeout.current[index]);
      }

      cleanDragOverTimeout.current[index] = setTimeout(() => {
        dragOverItem.classList.remove(indicatorClass);
      }, 500);
    }
  };

  /**
   * Renders the validity period for an asset if enabled, showing the start and end dates formatted as strings.
   * @param {ContentItem} asset - The asset object.
   * @param {number} index - The index of the asset in the selected assets array.
   * @returns {JSX.Element|null}
   */
  const renderValidity = (asset, index) => {
    const validityText = asset =>
      asset.validityEnabled
        ? `${UIHelper.time.formatDate(asset.validityStart)} - ${UIHelper.time.formatDate(asset.validityEnd)}`
        : '';

    if (![ASSET_TYPES.image, ASSET_TYPES.sign, ASSET_TYPES.video].includes(asset.type)) {
      return validityText(asset);
    }

    /**
     * Generates the display text for the asset's validity period based on its start and end timestamps.
     * @param {Boolean} validityEnabled - Indicates if the validity period is enabled.
     */
    const setValidity = value => {
      asset.validityEnabled = value;
      onChangeHandler(asset);
    };

    /**
     * Handles changes to the start timestamp, ensuring the asset state is updated with the new validity start time in ISO string format.
     * @param {Date} date - The new start timestamp selected by the user.
     */
    const changeStartTs = date => {
      asset.validityStart = date.toISOString();
      onChangeHandler(asset);
    };

    /**
     * Handles changes to the end timestamp, ensuring the asset state is updated with the new validity end time in ISO string format.
     * @param {Date} date - The new end timestamp selected by the user.
     */
    const changeEndTs = date => {
      asset.validityEnd = date.toISOString();
      onChangeHandler(asset);
    };

    const onChangeHandler = editedAsset => {
      onValidityUpdate(index, editedAsset);
    };

    return (
      <AssetValidityFormComponent
        enabled={Boolean(asset.validityEnabled)}
        setValidity={setValidity}
        validityStart={new Date(asset.validityStart)}
        changeStartTs={changeStartTs}
        validityEnd={new Date(asset.validityEnd)}
        changeEndTs={changeEndTs}
        withPortal
      />
    );
  };

  /**
   * Generates a unique key for a carousel item based on the asset ID and index.
   * @param {ContentItem} asset - The asset object.
   * @param {number} index - The index of the asset.
   * @returns {string} A unique key for the carousel item.
   */
  const getUniqueKey = (asset, index) => `${asset.id}-${asset.type}-${index}`;
  /**
   * Renders a single carousel item.
   * @param {ContentItem} asset - The asset object.
   * @param {number} index - The index of the asset.
   * @returns {JSX.Element} The carousel item UI.
   */
  const renderCarouselItem = (asset, index) => (
    <div
      className="carousel-item"
      key={getUniqueKey(asset, index)}
      draggable
      onDragStart={e => handleDragStart(e, index)}
      onDragOver={handleDragOver}
      onDrop={e => handleDrop(e, index)}
      onDragEnd={handleDragEnd}
      role="none"
      data-test={`asset-carousel-item-${getUniqueKey(asset, index)}`}
    >
      <button
        type="button"
        className="carousel-remove"
        onClick={() => onRemove(index)}
        aria-label={`Remove ${asset.name}`}
        data-test="asset-carousel-remove-button"
      >
        <span className={`${mui.className} small-button`}>{mui.icons.remove}</span>
      </button>
      <span className={`carousel-asset-type ${mui.className}`} title={asset.type}>
        {mui.icons[asset.type]}
      </span>
      <div className="carousel-thumb">
        <Thumbnail asset={asset} draggable={false} />
      </div>
      <div className="carousel-meta">
        {UIHelper.asset.getAssetNameEle(asset, 'asset-carousel', true)}
        <div className="carousel-asset-validity" title="Validity">
          {renderValidity(asset, index)}
        </div>
        <div className="carousel-input-container" title="Duration">
          {renderDurationInput(asset, index)}s
        </div>
        {asset.resolutionMismatch ? (
          <div className="carousel-asset-resolution" title="Resolution">
            <span
              className={`${mui.className} icon-warning`}
              title="Resolution mismatch"
              data-test="resolution-warning"
            >
              {mui.icons.warning_mark}
            </span>

            <span data-test="asset-carousel-resolution">
              {UIHelper.asset.formatResolution(asset.width, asset.height)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (wizardData.selectedAssets.length === 0) {
    return (
      <div className="asset-selection-carousel carousel-empty" data-test="asset-carousel-track">
        <p>Select assets to build your deployment playlist.</p>
      </div>
    );
  }

  const validationResult = wizardData.isAssetSelectionValid();
  const errorMsg =
    validationResult?.isValid || !validationResult ? (
      ''
    ) : (
      <span className="text-danger" data-error-code={validationResult.code}>
        <i className={`${mui.className}`}>{mui.icons.error}</i>
        <span>{validationResult.message}</span>
      </span>
    );

  return (
    <div className="asset-selection-carousel">
      <div className="carousel-header">
        <div className="carousel-header-left">
          <span>Selected Assets</span>
          {errorMsg}
        </div>
        <span>{wizardData.selectedAssets.length} chosen</span>
      </div>
      <div className="carousel-track" data-test="asset-carousel-track">
        {wizardData.selectedAssets.map((asset, index) => renderCarouselItem(asset, index))}
      </div>
    </div>
  );
}

AssetCarousel.propTypes = {
  wizardData: PropTypes.instanceOf(WizardData).isRequired,
  onRemove: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onDurationUpdate: PropTypes.func.isRequired,
  onValidityUpdate: PropTypes.func.isRequired,
};

export default AssetCarousel;
